/**
 * ScheduledScreenshotWatcher
 *
 * While the game is idle (between launch-macro completion and player join),
 * periodically runs the campaign's locked `watcher` tree natively in
 * `local-shadow` mode through `WatcherCheckRunner`, and emits a mismatch
 * event that PavilionCoordinator consumes to trigger a clean game restart.
 *
 * State machine:
 *   idle → active   (gameReady + watcher enabled + restartOnDisconnect ok)
 *   active → paused (player joins / stream starts; cancels an in-flight run)
 *   paused → idle   (stream stops with restartOnDisconnect=false, or game closed)
 *   active → idle   (game closed / campaign cleared / mismatch detected)
 *
 * Architecture: Coordinator pattern — DI constructor, event subscriptions,
 * public events, private field state.
 */

import * as Sentry from '@sentry/react';
import type { WatcherResult } from '@playcastdotio/Messaging';
import { CoordinatorEventImpl } from '../_types/events';
import type { GameLaunchCoordinator } from '../game-launch/GameLaunchCoordinator';
import type { PavilionCoordinator } from '../pavilion/PavilionCoordinator';
import type { SessionCoordinator } from '../session/SessionCoordinator';
import type { WatcherCheckRunner } from './WatcherCheckRunner';
import type { WatcherCheckErrorEvent, WatcherCheckPassedEvent, WatcherMismatchEvent } from './types';

const DEFAULT_WATCHER_INTERVAL_MS = 300_000;

export interface ScheduledScreenshotWatcherDeps {
  checkRunner: Pick<WatcherCheckRunner, 'check' | 'cancel'>;
  gameLaunchCoordinator: GameLaunchCoordinator;
  pavilionCoordinator: PavilionCoordinator;
  sessionCoordinator: SessionCoordinator;
}

export class ScheduledScreenshotWatcher {
  private readonly checkRunner: Pick<WatcherCheckRunner, 'check' | 'cancel'>;
  private readonly gameLaunchCoordinator: GameLaunchCoordinator;
  private readonly pavilionCoordinator: PavilionCoordinator;
  private readonly sessionCoordinator: SessionCoordinator;

  private active = false;
  private checkInFlight = false;
  private campaignId: string | null = null;
  private lastCheckResult: WatcherResult['outcome'] | null = null;
  private checksPerformed = 0;
  private lifecycleGeneration = 0;

  private readonly disposers: Array<() => void> = [];
  private tickTimeout: ReturnType<typeof setTimeout> | null = null;
  private initialized = false;

  public events = {
    watcherMismatchDetected: new CoordinatorEventImpl<WatcherMismatchEvent>(),
    watcherCheckPassed: new CoordinatorEventImpl<WatcherCheckPassedEvent>(),
    watcherCheckErrored: new CoordinatorEventImpl<WatcherCheckErrorEvent>(),
    watcherStarted: new CoordinatorEventImpl<{ campaignId: string }>(),
    watcherStopped: new CoordinatorEventImpl<{ reason: string }>(),
  };

  constructor(deps: ScheduledScreenshotWatcherDeps) {
    this.checkRunner = deps.checkRunner;
    this.gameLaunchCoordinator = deps.gameLaunchCoordinator;
    this.pavilionCoordinator = deps.pavilionCoordinator;
    this.sessionCoordinator = deps.sessionCoordinator;
  }

  /** Wire up event subscriptions. Idempotent. */
  public init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.disposers.push(
      this.gameLaunchCoordinator.events.gameReady.addListener(() => this.handleGameReady()),
      this.gameLaunchCoordinator.events.gameClosed.addListener(() => this.deactivate('game_closed')),
      this.sessionCoordinator.events.streamStarted.addListener(() => this.pause()),
      this.sessionCoordinator.events.streamStopped.addListener(() => this.deactivate('stream_stopped')),
    );
  }

  /** Tear down for tests */
  public destroy(): void {
    this.deactivate('destroyed');
    this.disposers.forEach((dispose) => dispose());
    this.disposers.length = 0;
    this.initialized = false;
  }

  /** Read-only view for debugging surfaces. */
  public getStatus(): { active: boolean; campaignId: string | null; checksPerformed: number; lastCheckResult: WatcherResult['outcome'] | null } {
    return { active: this.active, campaignId: this.campaignId, checksPerformed: this.checksPerformed, lastCheckResult: this.lastCheckResult };
  }

  // ==========================================================================
  // Event Handlers
  // ==========================================================================

  private handleGameReady(): void {
    const config = this.pavilionCoordinator.getCampaignConfig();
    if (!config?.scheduledWatcher?.enabled) return;

    // Watcher only makes sense when restart-on-disconnect is enabled.
    // Without it, post-player game state is non-deterministic.
    if (config.restartOnDisconnect === false) return;

    // If a stream is already active (player joined instantly), skip activation.
    if (this.sessionCoordinator.getStatus() === 'hosting') return;

    this.activate(config.campaignId);
  }

  // ==========================================================================
  // Lifecycle
  // ==========================================================================

  private activate(campaignId: string): void {
    if (this.active) return;

    this.lifecycleGeneration += 1;
    this.active = true;
    this.campaignId = campaignId;
    this.checksPerformed = 0;
    this.lastCheckResult = null;
    this.checkInFlight = false;

    console.log(`[ScheduledScreenshotWatcher] ▶ Activated for campaign ${campaignId}`);
    this.events.watcherStarted.emit({ campaignId });
    this.scheduleNextTick();
  }

  private pause(): void {
    if (!this.active) return;
    this.stop();
    console.log('[ScheduledScreenshotWatcher] ⏸ Paused (player joined)');
    this.events.watcherStopped.emit({ reason: 'player_joined' });
  }

  private deactivate(reason: string): void {
    if (!this.active) return;
    this.stop();
    this.campaignId = null;
    console.log(`[ScheduledScreenshotWatcher] ⏹ Deactivated: ${reason}`);
    this.events.watcherStopped.emit({ reason });
  }

  /** Clears the tick and cancels any in-flight native watcher run. */
  private stop(): void {
    this.lifecycleGeneration += 1;
    this.clearTick();
    this.active = false;
    if (this.checkInFlight) {
      this.checkInFlight = false;
      void this.checkRunner.cancel();
    }
  }

  // ==========================================================================
  // Tick / Check Loop
  // ==========================================================================

  private scheduleNextTick(): void {
    const config = this.pavilionCoordinator.getCampaignConfig();
    const intervalMs = config?.scheduledWatcher?.intervalMs ?? DEFAULT_WATCHER_INTERVAL_MS;
    this.tickTimeout = setTimeout(() => { void this.tick(); }, intervalMs);
  }

  private clearTick(): void {
    if (this.tickTimeout !== null) {
      clearTimeout(this.tickTimeout);
      this.tickTimeout = null;
    }
  }

  private async tick(): Promise<void> {
    if (!this.active) return;
    const reason = this.ineligibleReason();
    if (reason) return this.deactivate(reason);
    const generation = this.lifecycleGeneration, campaignId = this.campaignId!;

    this.checkInFlight = true;
    let result: WatcherResult;
    try {
      result = await this.checkRunner.check(campaignId);
    } catch (error) {
      result = { campaignId, outcome: 'error',
        errorCode: `watcher_check_threw:${error instanceof Error ? error.message : String(error)}`.slice(0, 128) };
    }
    // A new game may already be active when an old cancelled check settles.
    if (!this.active || generation !== this.lifecycleGeneration) return;
    this.checkInFlight = false;
    const changed = this.ineligibleReason();
    if (changed) return this.deactivate(changed);
    this.checksPerformed += 1;
    this.lastCheckResult = result.outcome;
    this.handleResult(result);
  }

  private ineligibleReason(): string | null {
    const config = this.pavilionCoordinator.getCampaignConfig();
    if (!config?.scheduledWatcher) return 'config_removed';
    if (!config.scheduledWatcher.enabled) return 'watcher_disabled';
    if (config.restartOnDisconnect === false) return 'restart_disabled';
    if (config.campaignId !== this.campaignId) return 'campaign_changed';
    return null;
  }

  private handleResult(result: WatcherResult): void {
    switch (result.outcome) {
      case 'pass':
        this.events.watcherCheckPassed.emit(result as WatcherCheckPassedEvent);
        this.scheduleNextTick();
        return;
      case 'mismatch':
        Sentry.logger.warn('[ScheduledScreenshotWatcher] Mismatch detected', { ...result });
        this.events.watcherMismatchDetected.emit(result as WatcherMismatchEvent);
        this.deactivate('mismatch_detected');
        return;
      case 'error':
        console.warn('[ScheduledScreenshotWatcher] Check errored — retrying next tick:', result.errorCode);
        this.events.watcherCheckErrored.emit(result as WatcherCheckErrorEvent);
        this.scheduleNextTick();
        return;
    }
  }
}
